import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'What is Drift?',
    answer:
      'Drift is a manual sleep tracking app that helps you understand and improve your sleep patterns. Unlike other sleep trackers, Drift does not require a wearable device or smartwatch. Simply log your bedtime and wake time each morning, rate your sleep quality, and record any factors that may have affected your sleep. Drift uses this data to calculate a smart sleep score, track your sleep debt, and reveal patterns over time.',
  },
  {
    question: 'How does the sleep score work?',
    answer:
      'Your sleep score is calculated from five key components: Duration (how long you slept compared to the recommended 7-9 hours), Timing (when you went to bed and woke up relative to your optimal sleep window), Consistency (how regular your sleep schedule is from day to day), Quality (your self-rated sleep quality), and Factors (the impact of factors you track such as caffeine, exercise, alcohol, stress, and screen time). Each component contributes to your overall score out of 100.',
  },
  {
    question: 'What is sleep debt?',
    answer:
      'Sleep debt is the accumulated deficit between the sleep you need and the sleep you actually get. Drift tracks your sleep debt using a battery metaphor — think of your sleep reserves as a battery that charges when you sleep well and drains when you don\'t get enough rest. A full battery means you\'re well-rested, while a low battery means you\'ve built up a deficit that needs recovering. Sleep debt is a rolling calculation based on your recent sleep history, not just a single night.',
  },
  {
    question: 'What\'s included in Premium?',
    answer:
      'Drift Premium unlocks: Score Breakdown (detailed breakdown by component), 30-Day Trends (sleep patterns, scores, and averages), Correlation Insights (how caffeine, exercise, and stress affect your sleep), Sleep Debt Recovery Plans (personalised guidance), and Extended History (complete sleep history). Pricing: £4.99/month, £39.99/year (save 33%), or £99.99 lifetime (one-time purchase).',
  },
  {
    question: 'How do I cancel my subscription?',
    answer:
      'On iOS: open Settings on your iPhone, tap your name, tap Subscriptions, select Drift - Sleep Tracker, and tap Cancel Subscription. On the web: open Drift, go to Settings, tap Manage Subscription to open the Stripe customer portal where you can cancel, switch plans, or update your payment method. Your premium features remain active until the end of your current billing period.',
  },
  {
    question: 'How do I delete my account?',
    answer:
      'Open Drift, go to Settings, tap Delete Account, and confirm the deletion. All your personal data including sleep logs, scores, and account information will be permanently removed from our servers within 30 days. Please cancel any active subscriptions before deleting your account.',
  },
  {
    question: 'Is my data secure?',
    answer:
      'Yes. Your data is stored securely using Amazon Web Services (AWS) in the EU West 2 (London) region. All data is encrypted at rest in DynamoDB and encrypted in transit using TLS 1.2+. Authentication is managed through AWS Cognito with secure token-based access. We are fully GDPR compliant — you can access, export, or delete your data at any time. We do not sell or share your data with third parties.',
  },
];

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className={`faq__item ${isOpen ? 'faq__item--open' : ''}`} onClick={onClick}>
    <div className="faq__question">
      <h3>{question}</h3>
      <svg
        className="faq__chevron"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
    {isOpen && (
      <div className="faq__answer">
        <p>{answer}</p>
      </div>
    )}
  </div>
);

const DriftSupport = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Support</span>
          <h1 className="legal-page__title">Drift Support</h1>
          <p className="legal-page__subtitle">
            Get help with Drift - Sleep Tracker by Wolf Technologies Ltd
          </p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          {/* Contact Section */}
          <section className="support-contact">
            <div className="support-contact__card">
              <div className="support-contact__icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <div>
                <h3>Need help?</h3>
                <p>
                  Contact us at{' '}
                  <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
                </p>
                <p className="support-contact__response">
                  We aim to respond to all support requests within 48 hours.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="legal-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq__list">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onClick={() => toggleFAQ(index)}
                />
              ))}
            </div>
          </section>

          {/* App Info */}
          <section className="legal-section">
            <h2>App Information</h2>
            <div className="support-info__grid">
              <div className="support-info__item">
                <span className="support-info__label">App</span>
                <span className="support-info__value">Drift - Sleep Tracker</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Version</span>
                <span className="support-info__value">1.0</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Developer</span>
                <span className="support-info__value">Wolf Technologies Ltd</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Platform</span>
                <span className="support-info__value">iOS</span>
              </div>
            </div>
          </section>

          {/* Links */}
          <section className="legal-section">
            <h2>Useful Links</h2>
            <div className="support-links">
              <Link to="/drift/privacy" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                Privacy Policy
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </Link>
              <a href="/" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                WolfTechnologies Home
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default DriftSupport;
